import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'
import { MERCHANT_ENDPOINTS } from './merchant.endpoints'
import type {
  MerchantCategory,
  MerchantProductsParams,
  MerchantProductsResponse,
  MerchantProfile,
  MerchantReviewsParams,
  MerchantReviewsResponse,
  NearbyMerchantsParams,
  NearbyMerchantsResponse,
} from './merchant.types'

// --- Service Functions ---

export const getMerchantProfile = async (slug: string): Promise<MerchantProfile> => {
  const response = await apiClient.get<MerchantProfile>(MERCHANT_ENDPOINTS.PROFILE(slug))
  return response.data
}

export const getMerchantCategories = async (slug: string): Promise<MerchantCategory[]> => {
  const response = await apiClient.get<MerchantCategory[]>(MERCHANT_ENDPOINTS.CATEGORIES(slug))
  return response.data
}

export const getMerchantProducts = async (
  slug: string,
  params?: MerchantProductsParams,
): Promise<MerchantProductsResponse> => {
  const response = await apiClient.get<MerchantProductsResponse>(MERCHANT_ENDPOINTS.PRODUCTS(slug), { params })
  return response.data
}

export const getMerchantReviews = async (
  slug: string,
  params?: MerchantReviewsParams,
): Promise<MerchantReviewsResponse> => {
  const response = await apiClient.get<MerchantReviewsResponse>(MERCHANT_ENDPOINTS.REVIEWS(slug), { params })
  return response.data
}

export const getNearbyMerchants = async (params: NearbyMerchantsParams): Promise<NearbyMerchantsResponse> => {
  const response = await apiClient.get<NearbyMerchantsResponse>(MERCHANT_ENDPOINTS.NEARBY_MERCHANTS, { params })
  return response.data
}

// --- TanStack Query Hooks ---

export const MERCHANT_QUERY_KEYS = {
  ROOT: ['merchant'],
  PROFILE: (slug: string) => ['merchant', slug, 'profile'],
  CATEGORIES: (slug: string) => ['merchant', slug, 'categories'],
  PRODUCTS: (slug: string) => ['merchant', slug, 'products'],
  REVIEWS: (slug: string) => ['merchant', slug, 'reviews'],
  NEARBY: (params: NearbyMerchantsParams) => ['merchant', 'nearby', params],
} as const

export const useMerchantProfileQuery = (
  slug: string,
  options?: Omit<UseQueryOptions<MerchantProfile>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: MERCHANT_QUERY_KEYS.PROFILE(slug),
    queryFn: () => getMerchantProfile(slug),
    enabled: !!slug,
    ...options,
  })
}

export const useMerchantCategoriesQuery = (
  slug: string,
  options?: Omit<UseQueryOptions<MerchantCategory[]>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: MERCHANT_QUERY_KEYS.CATEGORIES(slug),
    queryFn: () => getMerchantCategories(slug),
    enabled: !!slug,
    ...options,
  })
}

export const useMerchantProductsQuery = (
  slug: string,
  params?: MerchantProductsParams,
  options?: Omit<UseQueryOptions<MerchantProductsResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: [...MERCHANT_QUERY_KEYS.PRODUCTS(slug), params],
    queryFn: () => getMerchantProducts(slug, params),
    enabled: !!slug,
    ...options,
  })
}

export const useMerchantReviewsQuery = (
  slug: string,
  params?: MerchantReviewsParams,
  options?: Omit<UseQueryOptions<MerchantReviewsResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: [...MERCHANT_QUERY_KEYS.REVIEWS(slug), params],
    queryFn: () => getMerchantReviews(slug, params),
    enabled: !!slug,
    ...options,
  })
}

export const useNearbyMerchantsQuery = (
  params: NearbyMerchantsParams,
  options?: Omit<UseQueryOptions<NearbyMerchantsResponse>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery({
    queryKey: MERCHANT_QUERY_KEYS.NEARBY(params),
    queryFn: () => getNearbyMerchants(params),
    enabled: !!params.lat && !!params.lng,
    ...options,
  })
}
